import React, { useCallback, useContext, useRef } from 'react'

import { useForm } from 'react-hook-form'

import { spacing, Stack } from '@cash-design-system/react'
import { SubmitFormRequestElementResult } from '@cash-web/protos/squareup/franklin/app/submit_form.pb'
import { BlockerAction } from '@cash-web/protos/squareup/franklin/blocker_action.pb'
import { FormBlocker as FormBlockerProps } from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import logger from '@cash-web/shared/util-logger'
import { ErrorContext } from '@cash-web/shared-data-access-error'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'
import { useJourneyContext } from '@cash-web/shared-journey-data-access-context'

import { BaseBlocker } from '../BaseBlocker/BaseBlocker'
import { ButtonElement } from '../FormBlocker/elements/ButtonElement/ButtonElement'
import { FormBlockerElements } from '../FormBlocker/FormBlocker'
import { handleFormAction } from '../FormBlocker/handleFormAction'
import { removeUnsupportedActions } from '../FormBlocker/removeUnsupportedActions'
import { RecoveryCodes } from './RecoveryCodes'

export function BlockerTotpRecoveryCodes({ blocker }: { blocker: FormBlockerProps }): React.ReactElement {
  const form = useForm()
  const { setError, clearError } = useContext(ErrorContext)
  const journey = useJourneyContext()
  const flow = useFlowContext()
  const selectedAction = useRef('')

  const onContinueAction = useCallback(
    async (action: BlockerAction) => {
      const onBlockerAction = async (action: BlockerAction, results?: SubmitFormRequestElementResult[]) => {
        return (await flow.executeBlockerAction(action, results)) ?? null
      }
      const { onFormResponse, onSubmit } = await handleFormAction(action, onBlockerAction, setError, clearError)

      if (action.submit_action) {
        await form.handleSubmit(data => onSubmit(data))()
      } else {
        onFormResponse(await onBlockerAction(action))
      }
    },
    [setError, clearError, flow, form]
  )

  const recoveryCodes: string[] = []

  const cleanBlocker = removeUnsupportedActions(blocker, journey)

  const blockerElements = cleanBlocker.elements?.filter(element => {
    const text = element?.text_element?.text

    // We'd like to style the recovery codes a little differently, so this attempts to extract them from the provided text elements.
    // Recovery codes should only ever be a length of exactly 4 chars, and its unlikely any other form blocker text will be of exactly 4 chars.
    if (text?.length === 4) {
      recoveryCodes.push(text)
      return false
    }

    return element
  })

  const buttons = []
  if (cleanBlocker.primary_action) {
    buttons.push(
      <ButtonElement
        action={cleanBlocker.primary_action}
        canSubmit={!!cleanBlocker.primary_action.submit_action}
        key="primary"
        selectedAction={selectedAction}
      />
    )
  }

  if (recoveryCodes.length === 0) {
    logger.error(
      new Error('Unable to identify TOTP recovery codes, falling back to displaying unstyled recovery codes')
    )
  }

  return (
    <BaseBlocker
      form={form}
      onSubmit={async () => {
        if (cleanBlocker.primary_action) {
          return await onContinueAction(cleanBlocker.primary_action)
        }
      }}
      buttons={buttons}
    >
      <Stack space={spacing.defaultContent}>
        <FormBlockerElements blocker={{ ...blocker, elements: blockerElements }} />
        {recoveryCodes.length > 0 && <RecoveryCodes recoveryCodes={recoveryCodes} />}
      </Stack>
    </BaseBlocker>
  )
}
